// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { AuthContext } from '../../context/AuthContext';

import './Dropdown.scss';

export default function SupplierFilter({ occurringSuppliers, setSupplierFilter }) {
    const { t } = useTranslation();
    const { logEventCustom, suppliers } = React.useContext(AuthContext);
    const [selectedSupplierDisplayname, setSelectedSupplierDisplayname] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    function handleSupplierChange(supplier_id) {
        setSelectedSupplierDisplayname(getSupplierDisplayName(supplier_id));
        setSupplierFilter(supplier_id);
        setIsOpen(false);
        setSearchQuery('');

        // Log the event to Firebase Analytics
        logEventCustom("filter", {type: "supplier", value: supplier_id});
    }

    function getSupplierDisplayName(supplier_id) {
        if (!supplier_id || !suppliers?.[supplier_id]) {
            return t("material_filter_all");
        }
        if (suppliers?.[supplier_id]?.supplier_code) {
            return `${suppliers[supplier_id].supplier_code} ${suppliers[supplier_id].display_name}`;
        } else {
            return suppliers[supplier_id].display_name;
        }
    }

    function handleSearchChange(e) {
        setSearchQuery(e.target.value);
    }

    function clearSearch(e) {
        e.stopPropagation(); // Prevent toggling the dropdown
        setSearchQuery('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }

    function toggleDropdown() {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        
        if (newIsOpen && searchInputRef.current) {
            // Focus the search input when opening dropdown
            // Use RAF instead of setTimeout for better performance
            requestAnimationFrame(() => {
                searchInputRef.current.focus();
            });
        }
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return; // Only add listener when dropdown is open
        
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Sort suppliers by their display names
    const sortedSuppliers = React.useMemo(() => {
        return occurringSuppliers
            .filter(supplier_id => supplier_id !== undefined && supplier_id !== "")
            .sort((a, b) => {
                const displayNameA = getSupplierDisplayName(a);
                const displayNameB = getSupplierDisplayName(b);
                return displayNameA.localeCompare(displayNameB, undefined, {numeric: true});
            });
    }, [suppliers, getSupplierDisplayName]);

    // Filter suppliers based on search query
    const filteredSuppliers = React.useMemo(() => {
        if (!searchQuery) return sortedSuppliers;
        
        const query = searchQuery.toLowerCase();
        return sortedSuppliers.filter(supplier_id => {
            const displayName = getSupplierDisplayName(supplier_id).toLowerCase();
            const id = supplier_id.toLowerCase();
            return displayName.includes(query) || id.includes(query);
        });
    }, [sortedSuppliers, searchQuery, getSupplierDisplayName]);

    return (
        <div className="dropdown" ref={dropdownRef}>
            <div className="dropdown-splitbutton" onClick={toggleDropdown}>
                <button className="dropdown-splitbutton-text">{t("supplier_filter_title")}:</button>
                <button className="dropdown-splitbutton-button">
                    {selectedSupplierDisplayname ? selectedSupplierDisplayname : t("material_filter_all")}
                </button>
            </div>
            <div className={`dropdown-content ${isOpen ? 'dropdown-content-open' : ''}`}>
                <div className="dropdown-search">
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t("search_suppliers")}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onClick={(e) => e.stopPropagation()}
                        className="dropdown-search-input"
                    />
                    {searchQuery && (
                        <button 
                            className="dropdown-search-clear" 
                            onClick={clearSearch}
                            aria-label={t("clear_search")}
                        >
                            ×
                        </button>
                    )}
                </div>
                <a onClick={() => handleSupplierChange("")}>{t("material_filter_all")}</a>
                {filteredSuppliers.map(supplier => (
                    <a key={supplier} onClick={() => handleSupplierChange(supplier)}>
                        {getSupplierDisplayName(supplier)}
                    </a>
                ))}
                {filteredSuppliers.length === 0 && searchQuery && (
                    <div className="dropdown-no-results">{t("no_results_found")}</div>
                )}
            </div>
        </div>
    );
}